import { useEffect } from "react";
import { toast } from "sonner";
import { notificationUrl, pendingReminderDelivery } from "@/lib/reminderDelivery";
import {
  deletePushSubscriptions,
  getPushConfig,
  getReminders,
  savePushSubscription,
} from "@/lib/apiClient";

export const REMINDER_NOTIFICATIONS_KEY = "acai-reminder-notifications-enabled";
const NOTIFIED_KEY = "acai-reminders-notified";
export const PRIVATE_NOTIFICATIONS_KEY = "acai-private-notifications";

const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone === true;

const urlBase64ToUint8Array = (value) => {
  const padding = "=".repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((character) => character.charCodeAt(0)));
};

const readNotified = () => {
  try { return JSON.parse(localStorage.getItem(NOTIFIED_KEY) || "[]"); }
  catch { return []; }
};

const showReminder = async (reminder) => {
  const options = {
    body: localStorage.getItem(PRIVATE_NOTIFICATIONS_KEY) === "false" ? reminder.title : "Sinulla on muistutus. Avaa ACAI nähdäksesi tiedot.",
    icon: "/assets/acai-app-icon-192.png",
    badge: "/assets/acai-app-icon-192.png",
    tag: `acai-reminder-${reminder.id}-${reminder.remind_at}`,
    data: { url: notificationUrl(reminder.id) },
  };
  if ("serviceWorker" in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification("ACAI-muistutus", options);
    return;
  }
  // Desktop fallback for browsers without service workers.
  const notification = new Notification("ACAI-muistutus", options);
  notification.onclick = () => { notification.close(); window.focus(); window.location.assign(options.data.url); };
};

export const reminderNotificationsEnabled = () =>
  typeof window !== "undefined" &&
  "Notification" in window &&
  Notification.permission === "granted" &&
  localStorage.getItem(REMINDER_NOTIFICATIONS_KEY) === "true";

export const requestReminderNotifications = async (knownConfig = null) => {
  if (!("Notification" in window) || !("serviceWorker" in navigator) || !("PushManager" in window)) {
    return "unsupported";
  }
  if (isIos() && !isStandalone()) return "install_required";
  // A preloaded config keeps the permission request in the user's tap gesture
  // on mobile browsers. Older callers can still fetch configuration here.
  const config = knownConfig || await getPushConfig();
  if (!config.enabled || !config.public_key) return "unconfigured";
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    localStorage.setItem(REMINDER_NOTIFICATIONS_KEY, "false");
    return permission;
  }
  const registration = await navigator.serviceWorker.ready;
  let subscription = await registration.pushManager.getSubscription();
  if (!subscription) {
    subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(config.public_key),
    });
  }
  await savePushSubscription(subscription.toJSON());
  localStorage.setItem(REMINDER_NOTIFICATIONS_KEY, "true");
  return permission;
};

export const disableReminderNotifications = async () => {
  if ("serviceWorker" in navigator) {
    // Unlike `ready`, this also resolves when no worker has been installed.
    const registration = await navigator.serviceWorker.getRegistration();
    const subscription = await registration?.pushManager?.getSubscription();
    if (subscription) {
      // Keep the endpoint available for retry if the server cannot be reached.
      // Never disable every device when this browser has no subscription.
      await deletePushSubscriptions(subscription.endpoint);
      await subscription.unsubscribe();
    }
  }
  localStorage.setItem(REMINDER_NOTIFICATIONS_KEY, "false");
};

export const ReminderNotifier = () => {
  useEffect(() => {
    let active = true;
    let busy = false;
    // Never reuse the previous account's less-private choice during loading.
    localStorage.setItem(PRIVATE_NOTIFICATIONS_KEY, "true");
    getPushConfig().then((config) => {
      if (active) localStorage.setItem(PRIVATE_NOTIFICATIONS_KEY, String(config.private_content !== false));
    }).catch(() => { localStorage.setItem(PRIVATE_NOTIFICATIONS_KEY, "true"); });

    const check = async () => {
      if (!active || busy) return;
      busy = true;
      try {
        const reminders = await getReminders();
        if (!active) return;
        const notified = new Set(readNotified());
        const now = Date.now();
        const { due, fresh, older } = pendingReminderDelivery(reminders, notified, now);
        // The in-flight lock covers asynchronous delivery of this batch.
        due.forEach((reminder) => notified.add(`${reminder.id}:${reminder.remind_at}`));
        const currentKeys = new Set(reminders.map((reminder) => `${reminder.id}:${reminder.remind_at}`));
        localStorage.setItem(NOTIFIED_KEY, JSON.stringify([...notified].filter((key) => currentKeys.has(key))));
        if (older.length) toast.info(`${older.length} aiempaa muistutusta odottaa.`, { action: { label: "Avaa muistutukset", onClick: () => window.location.assign("/reminders") } });
        for (const reminder of fresh) {
          const key = `${reminder.id}:${reminder.remind_at}`;
          if (active) {
            if (reminderNotificationsEnabled()) {
              try { await showReminder(reminder); } catch { /* The in-app reminder still appears below. */ }
            }
            toast.info(`Muistutus: ${reminder.title}`);
            notified.add(key);
          }
        }
      } catch {
        // A reminder check must never interrupt normal app use.
      } finally {
        busy = false;
      }
    };

    check();
    const interval = window.setInterval(check, 30_000);
    const onVisibility = () => { if (document.visibilityState === "visible") check(); };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  return null;
};
